// Trust Autopilot — nightly trust score with checks and one-click fixes (GET/POST /api/admin/trust/autopilot*).
import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, RefreshCw, Loader2, CheckCircle2, AlertTriangle, XCircle, Info, Wrench, ArrowRight, Mail } from 'lucide-react';
import { apiCall } from './_shared';

const STATUS = {
  ok: { icon: CheckCircle2, cls: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
  warn: { icon: AlertTriangle, cls: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  fail: { icon: XCircle, cls: 'text-red-600', bg: 'bg-red-50 border-red-200' },
  info: { icon: Info, cls: 'text-slate-500', bg: 'bg-white border-slate-200' },
};
const ORDER = { fail: 0, warn: 1, info: 2, ok: 3 };

const ScoreRing = ({ score }) => {
  const tone = score >= 85 ? '#059669' : score >= 65 ? '#d97706' : '#dc2626';
  const r = 34, c = 2 * Math.PI * r;
  return (
    <svg width="88" height="88" viewBox="0 0 88 88" className="flex-shrink-0" data-testid="trust-score-ring">
      <circle cx="44" cy="44" r={r} stroke="#e2e8f0" strokeWidth="8" fill="none" />
      <circle cx="44" cy="44" r={r} stroke={tone} strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - score / 100)} transform="rotate(-90 44 44)" style={{ transition: 'stroke-dashoffset 600ms ease' }} />
      <text x="44" y="49" textAnchor="middle" fontSize="20" fontWeight="700" fill="#0f172a" data-testid="trust-score-value">{score}</text>
    </svg>
  );
};

const GoogleQuickEntry = ({ onSaved }) => {
  const [v, setV] = useState({ rating: '', count: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    try {
      await apiCall('/api/admin/google-rating', { method: 'PUT', body: JSON.stringify({ rating: Number(v.rating), count: Number(v.count) }) });
      await onSaved();
    } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
  };
  const ok = Number(v.rating) >= 1 && Number(v.rating) <= 5 && Number(v.count) >= 1;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="google-quick-entry">
      <span className="text-slate-600">Type what Google Maps shows today:</span>
      <input type="number" step="0.1" min="1" max="5" placeholder="e.g. 3.7" value={v.rating} onChange={(e) => setV({ ...v, rating: e.target.value })} className="w-20 border rounded px-2 py-1" data-testid="google-rating-input" />
      <span className="text-slate-500">★ ·</span>
      <input type="number" min="1" placeholder="reviews, e.g. 163" value={v.count} onChange={(e) => setV({ ...v, count: e.target.value })} className="w-32 border rounded px-2 py-1" data-testid="google-count-input" />
      <button type="button" onClick={save} disabled={!ok || busy} className="bg-slate-900 text-white rounded px-2.5 py-1 font-semibold hover:bg-orange-600 disabled:opacity-50" data-testid="google-rating-save">{busy ? '…' : 'Save'}</button>
      <a href="https://www.google.com/maps/search/?api=1&query=Hotel+La+Rosa+Negra+Ulcinj" target="_blank" rel="noopener noreferrer" className="underline text-slate-600 hover:text-orange-600" data-testid="google-rating-open-maps">Open Google Maps</a>
      {err && <span className="text-red-600 basis-full" data-testid="google-rating-error">{err}</span>}
    </div>
  );
};

const CheckRow = ({ c, busy, onFix, onOpenTab, canEdit, onReload }) => {
  const s = STATUS[c.status] || STATUS.info;
  const Icon = s.icon;
  return (
    <li className={`rounded-lg border p-3 flex flex-col sm:flex-row sm:items-start gap-2 ${s.bg}`} data-testid={`trust-check-${c.id}`}>
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.cls}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900">{c.title}</p>
        <p className="text-xs text-slate-600 mt-0.5">{c.detail}</p>
        {c.id === 'google_badge' && c.status !== 'ok' && canEdit && <GoogleQuickEntry onSaved={onReload} />}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {c.fix && canEdit && (
          <button type="button" onClick={() => onFix(c.fix)} disabled={busy} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-2.5 py-1 text-xs font-semibold hover:bg-orange-600 disabled:opacity-60" data-testid={`trust-fix-${c.id}`}>
            {busy === c.fix ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wrench className="w-3 h-3" />} {c.fix_label || 'Fix'}
          </button>
        )}
        {c.tab && onOpenTab && (
          <button type="button" onClick={() => onOpenTab(c.tab)} className="inline-flex items-center gap-1 border rounded px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-white" data-testid={`trust-open-${c.id}`}>
            {!c.fix && c.fix_label ? c.fix_label : 'Open'} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </li>
  );
};

const TrustAutopilotCard = ({ userRole, onOpenTab }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState(null);
  const canEdit = ['super_admin', 'admin', 'manager'].includes(userRole);

  const load = useCallback(async () => {
    try { const d = await apiCall('/api/admin/trust/autopilot'); setData(d); setForm(d.settings); } catch (e) { setMsg(String(e.message || e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (copy = false) => {
    setBusy('run'); setMsg('');
    try { const r = await apiCall(`/api/admin/trust/autopilot/run?copy=${copy}`, { method: 'POST' }); setMsg(`Audit done — score ${r.score}/100.`); await load(); }
    catch (e) { setMsg(String(e.message || e)); } finally { setBusy(false); }
  };
  const fix = async (action) => {
    setBusy(action); setMsg('');
    try { const r = await apiCall(`/api/admin/trust/autopilot/fix/${action}`, { method: 'POST', body: JSON.stringify({}) }); setMsg(`Applied — score now ${r.score}/100.`); await load(); }
    catch (e) { setMsg(String(e.message || e)); } finally { setBusy(false); }
  };
  const saveSettings = async () => {
    setBusy('settings'); setMsg('');
    try { await apiCall('/api/admin/trust/autopilot/settings', { method: 'PUT', body: JSON.stringify(form) }); setMsg('Settings saved.'); await load(); }
    catch (e) { setMsg(String(e.message || e)); } finally { setBusy(false); }
  };

  if (!data) return <div className="bg-white rounded-xl border p-6 text-sm text-slate-500" data-testid="trust-autopilot-card">{msg || 'Loading trust autopilot…'}</div>;
  const latest = data.latest;
  const checks = [...(latest?.checks || [])].sort((a, b) => ORDER[a.status] - ORDER[b.status]);
  const rows = checks.map((c) => <CheckRow key={c.id} c={c} busy={busy} onFix={fix} onOpenTab={onOpenTab} canEdit={canEdit} onReload={() => run(false)} />);
  const history = (data.history || []).slice().reverse();
  const bars = history.map((h) => (
    <div key={h.at} title={`${h.at.slice(0, 10)} · ${h.score}`} className="flex-1 flex flex-col justify-end" data-testid="trust-history-bar">
      <div className={`${h.score >= 85 ? 'bg-emerald-500' : h.score >= 65 ? 'bg-amber-500' : 'bg-red-500'} rounded-t`} style={{ height: `${Math.max(6, h.score * 0.4)}px` }} />
    </div>
  ));
  const nFail = latest?.fails?.length || 0;
  const nWarn = latest?.warns?.length || 0;

  return (
    <div className="bg-white rounded-xl border p-5 space-y-4" data-testid="trust-autopilot-card">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        {latest ? <ScoreRing score={latest.score} /> : <ShieldCheck className="w-12 h-12 text-slate-300" />}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-orange-500" /> Trust Autopilot</h3>
          <p className="text-xs text-slate-500 mt-1">Runs every night at 06:45 after the Booking.com sync; the copy honesty audit runs on Mondays. {latest ? `Last run ${latest.at.slice(0, 16).replace('T', ' ')} UTC by ${latest.by}.` : 'Not run yet.'}</p>
          {latest && <p className="text-xs mt-1" data-testid="trust-summary"><span className="font-semibold text-red-600">{nFail} failing</span> · <span className="font-semibold text-amber-600">{nWarn} warnings</span> · {checks.length - nFail - nWarn} fine{latest.emailed ? ' · digest e-mailed' : ''}</p>}
        </div>
        <div className="flex items-center gap-2">
          {history.length > 1 && <div className="hidden sm:flex items-end gap-0.5 h-10 w-28" data-testid="trust-history">{bars}</div>}
          {canEdit && (
            <button type="button" onClick={() => run(false)} disabled={!!busy} className="inline-flex items-center gap-1 bg-slate-900 text-white rounded px-3 py-1.5 text-xs font-semibold hover:bg-orange-600 disabled:opacity-60" data-testid="trust-run-now">
              {busy === 'run' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Run now
            </button>
          )}
        </div>
      </div>

      {msg && <p className="text-xs text-slate-700 bg-slate-50 border rounded px-3 py-2" data-testid="trust-msg">{msg}</p>}

      {latest ? <ul className="space-y-2" data-testid="trust-checks">{rows}</ul> : <p className="text-sm text-slate-500">Press "Run now" to compute the first trust score.</p>}

      {data.copy_audit && (
        <p className="text-xs text-slate-500" data-testid="trust-copy-audit-line">
          Copy honesty audit: {data.copy_audit.flagged} of {data.copy_audit.checked} texts flagged on {data.copy_audit.at.slice(0, 10)}{data.copy_audit.errors ? ` (${data.copy_audit.errors} batches failed)` : ''}.
          {data.copy_audit.flagged > 0 && onOpenTab && <> <button type="button" onClick={() => onOpenTab('translations')} className="underline hover:text-orange-600">Open Translations</button> to apply the plain rewrites.</>}
        </p>
      )}

      {canEdit && form && (
        <details className="border-t pt-3" data-testid="trust-settings">
          <summary className="text-xs font-semibold text-slate-700 cursor-pointer">Settings</summary>
          <div className="grid sm:grid-cols-2 gap-3 mt-3 text-xs">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="trust-set-enabled" /> Nightly run enabled</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.weekly_copy_audit} onChange={(e) => setForm({ ...form, weekly_copy_audit: e.target.checked })} data-testid="trust-set-copy" /> Weekly AI copy honesty audit (Mondays)</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.email_digest} onChange={(e) => setForm({ ...form, email_digest: e.target.checked })} data-testid="trust-set-digest" /> <Mail className="w-3 h-3" /> E-mail the owner only when a check starts failing{data.mail_ready ? '' : ' (mail not configured)'}</label>
            <label className="sm:col-span-2">Owner e-mail (blank = finance report address)<input value={form.owner_email || ''} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="block w-full border rounded px-2 py-1 mt-1" placeholder="owner@example.com" data-testid="trust-set-email" /></label>
          </div>
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={saveSettings} disabled={!!busy} className="bg-slate-900 text-white rounded px-3 py-1.5 text-xs font-semibold hover:bg-orange-600 disabled:opacity-60" data-testid="trust-settings-save">Save settings</button>
            <button type="button" onClick={() => run(true)} disabled={!!busy} className="border rounded px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60" data-testid="trust-run-copy">Run with copy audit (≈1 min)</button>
          </div>
        </details>
      )}
    </div>
  );
};

export default TrustAutopilotCard;
